import { Component, computed, EventEmitter, input, Output, Signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DisputeStatus, isRefundableDisputeStatus, isRefundableStatus, RetailPayment } from '@galaxy/billing';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { DatePipe } from '@angular/common';
import { MatAnchor, MatButton } from '@angular/material/button';
import { LexiconModule } from '@galaxy/lexicon';
import { MatCard } from '@angular/material/card';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { MatIcon } from '@angular/material/icon';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { PaymentCardComponent } from '../details-section/payment-card.component';
import { PaymentStatusChipComponent } from '../../../payment-status-chip/payment-status-chip.component';
import { ViewConfig } from '../shared/view-config';
import { PricePipe } from '../../../shared/pipes';

@Component({
  selector: 'smb-invoicing-payment-page',
  templateUrl: './payment-page-container.component.html',
  styleUrls: ['./payment-page-container.component.scss'],
  standalone: true,
  imports: [
    GalaxyPageModule,
    PaymentStatusChipComponent,
    MatButton,
    LexiconModule,
    MatAnchor,
    RouterLink,
    MatCard,
    GalaxyEmptyStateModule,
    MatIcon,
    GalaxyAlertModule,
    DatePipe,
    PaymentCardComponent,
    PricePipe,
  ],
})
export class PaymentPageContainerComponent {
  merchantId = input.required<string>();
  loading = input<boolean>(true);
  payment = input.required<RetailPayment>();
  paymentOutstanding = input.required<boolean>();
  errorCode = input<number>();
  paymentsPageLink = input.required<string>();
  viewConfig = input<ViewConfig>({
    hideRefundReference: false,
    hideDisputeReference: false,
    hideBusinessReference: false,
    hideInvoiceReference: false,
  });

  shouldDisplayRefundButton: Signal<boolean>;
  shouldDisplayAcceptDisputeButton: Signal<boolean>;
  shouldDisplayProvideEvidenceButton: Signal<boolean>;
  shouldDisplayViewEvidenceButton: Signal<boolean>;

  @Output() acceptDisputeClicked = new EventEmitter<void>();
  @Output() createRefundClicked = new EventEmitter<RetailPayment>();

  needsResponseWarningStatuses = [DisputeStatus.WARNING_NEEDS_RESPONSE, DisputeStatus.NEEDS_RESPONSE];

  constructor() {
    this.shouldDisplayRefundButton = computed(() => {
      if (!this.viewConfig().hideRefundReference && isRefundableStatus(this.payment().status)) {
        return true;
      }
      if (!this.viewConfig().hideDisputeReference && isRefundableDisputeStatus(this.payment().dispute?.status)) {
        return true;
      }
      return false;
    });

    this.shouldDisplayAcceptDisputeButton = computed(() => {
      if (!this.payment().dispute) {
        return false;
      }

      return this.viewConfig().hideDisputeReference
        ? false
        : this.payment().dispute?.status === DisputeStatus.NEEDS_RESPONSE;
    });

    this.shouldDisplayProvideEvidenceButton = computed(() => {
      if (!this.payment().dispute) {
        return false;
      }

      return this.viewConfig().hideDisputeReference
        ? false
        : this.needsResponseWarningStatuses.includes(this.payment().dispute?.status);
    });

    this.shouldDisplayViewEvidenceButton = computed(() => {
      if (!this.payment().dispute) {
        return false;
      }

      return this.viewConfig().hideDisputeReference
        ? false
        : !this.needsResponseWarningStatuses.includes(this.payment().dispute?.status);
    });
  }

  onAcceptDisputeClicked(): void {
    this.acceptDisputeClicked.emit();
  }

  onCreateRefundClicked(payment: RetailPayment): void {
    this.createRefundClicked.emit(payment);
  }
}
